import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stack,
  Alert,
  Chip,
  Autocomplete
} from '@mui/material';
import { updateMasterForm } from './FormManagementQueries';
import { getAllCorporateUsers } from '../CorporateQueries';

const EditFormDialog = ({ open, onClose, form, onSuccess }) => {
  const [formData, setFormData] = useState({
    form_name: '',
    form_type: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'draft'
  });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'closed', label: 'Closed' },
    { value: 'archived', label: 'Archived' }
  ];

  useEffect(() => {
    const loadFormData = async () => {
      if (open && form) {
        try {
          // Load all employees first
          const result = await getAllCorporateUsers();
          const usersList = Array.isArray(result) ? result : [];
          // Filter to show only employees with "Employee" role
          const employeeRoleOnly = usersList.filter(user => user.role === 'Employee');
          setEmployees(employeeRoleOnly);
          
          // Populate form data
          setFormData({
            form_name: form.form_name || '',
            form_type: form.form_type || '',
            description: form.description || '',
            start_date: form.start_date || '',
            end_date: form.end_date || '',
            status: form.status || 'draft'
          });

          // Parse and set assigned employees
          let assignedData = [];
          if (form.assigned_employees) {
            try {
              const parsed = typeof form.assigned_employees === 'string' 
                ? JSON.parse(form.assigned_employees) 
                : form.assigned_employees;
              assignedData = Array.isArray(parsed) ? parsed : [];
            } catch {
              assignedData = Array.isArray(form.assigned_employees) ? form.assigned_employees : [];
            }
          }

          // Extract emails from assigned data (could be strings or objects)
          const assignedEmails = assignedData.map(item => {
            if (typeof item === 'string') return item;
            return item.email || item.employee_email || '';
          }).filter(Boolean);

          // Match assigned emails to employee objects from the loaded list
          const assigned = employeeRoleOnly.filter(emp => assignedEmails.includes(emp.email));
          setSelectedEmployees(assigned);
        } catch (error) {
          console.error('Error loading form data:', error);
          setEmployees([]);
        }
      }
    };

    loadFormData();
  }, [open, form]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.form_name.trim()) {
      setError('Form name is required');
      return false;
    }
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return false;
    }
    if (!formData.start_date) {
      setError('Start date is required');
      return false;
    }
    if (!formData.end_date) {
      setError('End date is required');
      return false;
    }
    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      setError('End date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Store complete employee objects including reporting managers
      const employeeObjects = selectedEmployees.map(emp => ({
        email: emp.email,
        employee_email: emp.email,
        name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email,
        employee_name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email,
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        reporting_l1: emp.reporting_l1 || '',
        reporting_l2: emp.reporting_l2 || ''
      }));

      await updateMasterForm({
        id: form.id,
        form_name: formData.form_name,
        form_type: formData.form_type,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
        assigned_employees: employeeObjects,
        form_schema: form.form_schema || JSON.stringify({}),
        program_code: form.program_code || ''
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error updating form:', err);
      setError('Failed to update form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Form</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stack spacing={2.5}>
            {/* Form Name */}
            <TextField
              label="Form Name"
              value={formData.form_name}
              onChange={(e) => handleChange('form_name', e.target.value)}
              fullWidth
              required
              helperText="Enter a descriptive name for this form"
            />

            {/* Form Type - Read Only */}
            <TextField
              label="Form Type"
              value={formData.form_type === 'annual' ? 'Annual Performance Review' : 'Probation Progress Assessment'}
              fullWidth
              disabled
              helperText="Form type cannot be changed"
            />

            {/* Date Range */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="When can employees start filling this form?"
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Last date for form submission"
              />
            </Stack>

            {/* Employee Assignment */}
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(option) => `${option.first_name || ''} ${option.last_name || ''} (${option.email || ''})`}
              value={selectedEmployees}
              onChange={(_, newValue) => setSelectedEmployees(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign to Employees"
                  placeholder="Start typing to search employees..."
                  required
                  helperText="Select employees who will have access to this form"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.email}
                    label={`${option.first_name} ${option.last_name}`}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />

            {/* Status */}
            <FormControl fullWidth required>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                label="Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this form"
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Form'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFormDialog;
